// Sincronización entre la ventana del deck (audiencia) y la vista de presentador.
//
//  - Misma máquina: BroadcastChannel (mismo origen y navegador). Siempre activo.
//  - Otro dispositivo (teléfono): la vista de presentador abre una sesión en vivo y le
//    avisa a esta ventana por BroadcastChannel (`live`); entonces esta ventana se conecta
//    además por WebSocket a la sala de esa sesión.
//
// La audiencia es la fuente de verdad: publica su estado y obedece comandos de navegación.
// Por estos canales nunca pasan las notas. Ver docs/vista-presentador.md.

import { useEffect, useRef, useState } from "react";
import { openLiveSocket, type LiveSocket } from "@/hooks/liveSocket";

// Cronómetro compartido. Lo lleva la ventana del deck para que todas las vistas de
// presentador (otra ventana, teléfono) muestren el mismo tiempo. `startedAt` es epoch ms
// del reloj del deck, o null si está en pausa; `elapsedMs` es lo acumulado antes de eso.
export interface DeckTimer {
  elapsedMs: number;
  startedAt: number | null;
}

export type DeckSyncMessage =
  | { type: "hello" }
  | {
      type: "state";
      index: number;
      slideIds: string[];
      titles: string[];
      live?: string | null;
      timer?: DeckTimer;
      /** Reloj del deck al enviar: permite corregir el desfase de reloj entre equipos. */
      now?: number;
    }
  | { type: "timer"; action: "toggle" | "reset" }
  | { type: "next" }
  | { type: "prev" }
  | { type: "goto"; index: number }
  // Presentador -> audiencia (solo BroadcastChannel): únete a esta sesión en vivo, o
  // sal de ella si `session` es null.
  | { type: "live"; session: string | null };

export function deckChannelName(deckId: string, locale: string): string {
  return `sota-deck:${deckId}:${locale}`;
}

/** Abre el canal del deck, o null si el navegador no soporta BroadcastChannel. */
export function openDeckChannel(deckId: string, locale: string): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  return new BroadcastChannel(deckChannelName(deckId, locale));
}

const liveKey = (deckId: string) => `deck-live:${deckId}`;
const timerKey = (deckId: string) => `deck-timer:${deckId}`;

function readTimer(deckId: string): DeckTimer {
  try {
    const raw = window.sessionStorage.getItem(timerKey(deckId));
    if (raw) return JSON.parse(raw) as DeckTimer;
  } catch {
    // valor corrupto: se parte de cero
  }
  return { elapsedMs: 0, startedAt: null };
}

interface AudienceSyncOptions {
  deckId: string;
  locale: string;
  index: number;
  slideIds: string[];
  titles: string[];
  onNext: () => void;
  onPrev: () => void;
  onGoto: (index: number) => void;
}

/** Lado audiencia: publica el estado al cambiar de slide y obedece comandos. */
export function useDeckAudienceSync(options: AudienceSyncOptions): void {
  const { deckId, locale, index } = options;
  const latest = useRef(options);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const liveRef = useRef<LiveSocket | null>(null);
  // Sesión en vivo vigente; sobrevive a una recarga de esta pestaña.
  const [liveSession, setLiveSession] = useState<string | null>(() =>
    typeof window === "undefined" ? null : window.sessionStorage.getItem(liveKey(deckId)),
  );
  const liveSessionRef = useRef(liveSession);
  // Sobrevive a una recarga de la pestaña, igual que la sesión en vivo.
  const timerRef = useRef<DeckTimer | null>(null);
  timerRef.current ??=
    typeof window === "undefined" ? { elapsedMs: 0, startedAt: null } : readTimer(deckId);

  useEffect(() => {
    latest.current = options;
    liveSessionRef.current = liveSession;
  });

  const stateMessage = (): DeckSyncMessage => {
    const { index, slideIds, titles } = latest.current;
    return {
      type: "state",
      index,
      slideIds,
      titles,
      live: liveSessionRef.current,
      timer: timerRef.current ?? undefined,
      now: Date.now(),
    };
  };

  // Publica el estado por los dos canales (misma máquina y sala en vivo).
  const publish = () => {
    const message = stateMessage();
    channelRef.current?.postMessage(message);
    liveRef.current?.send(message);
  };

  const obey = (message: DeckSyncMessage) => {
    if (message.type === "next") latest.current.onNext();
    else if (message.type === "prev") latest.current.onPrev();
    else if (message.type === "goto") latest.current.onGoto(message.index);
    else if (message.type === "timer") {
      const current = timerRef.current ?? { elapsedMs: 0, startedAt: null };
      const now = Date.now();
      const next: DeckTimer =
        message.action === "reset"
          ? { elapsedMs: 0, startedAt: null }
          : current.startedAt !== null
            ? { elapsedMs: current.elapsedMs + (now - current.startedAt), startedAt: null }
            : { elapsedMs: current.elapsedMs, startedAt: now };
      timerRef.current = next;
      window.sessionStorage.setItem(timerKey(deckId), JSON.stringify(next));
      // Todas las vistas de presentador reciben el cronómetro nuevo.
      publish();
    }
  };

  // --- BroadcastChannel (misma máquina) ---
  useEffect(() => {
    const channel = openDeckChannel(deckId, locale);
    channelRef.current = channel;
    if (!channel) return;

    channel.onmessage = (event: MessageEvent<DeckSyncMessage>) => {
      const message = event.data;
      if (message.type === "hello") {
        channel.postMessage(stateMessage());
      } else if (message.type === "live") {
        if (message.session) window.sessionStorage.setItem(liveKey(deckId), message.session);
        else window.sessionStorage.removeItem(liveKey(deckId));
        setLiveSession(message.session);
      } else {
        obey(message);
      }
    };

    channel.postMessage(stateMessage());
    return () => {
      channel.close();
      channelRef.current = null;
    };
    // obey y publish solo leen refs (y deckId, que ya es dependencia).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId, locale]);

  // --- WebSocket (otro dispositivo), solo mientras hay sesión en vivo ---
  useEffect(() => {
    if (!liveSession) return;
    const socket = openLiveSocket({
      deckId,
      session: liveSession,
      role: "audience",
      onOpen: () => socket.send(stateMessage()),
      onMessage: (message) => {
        if (message.type === "hello") socket.send(stateMessage());
        else obey(message);
      },
    });
    liveRef.current = socket;
    // Confirma la sesión a la vista de presentador de esta máquina.
    channelRef.current?.postMessage(stateMessage());
    return () => {
      socket.close();
      liveRef.current = null;
    };
    // obey y publish solo leen refs (y deckId, que ya es dependencia).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId, liveSession]);

  // Estado nuevo en cada cambio de slide, por ambos canales.
  useEffect(() => {
    publish();
    // obey y publish solo leen refs (y deckId, que ya es dependencia).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);
}
